import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DatasetService } from 'src/app/services/dataset.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bhub-checkbox-cell',
  standalone: false,
  templateUrl: './checkbox-cell.component.html',
  styleUrl: './checkbox-cell.component.scss'
})
export class CheckboxCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();
  
  // UI Properties
  labelText: string = '';
  isChecked: boolean = false;
  checkedValue: any = true;
  uncheckedValue: any = false;
  
  // Configuration properties
  dataSource: 'context' | 'dataset' | 'none' = 'none';
  contextObjectKey: string = '';
  fieldName: string = '';
  datasetId: number | null = null;
  
  // State
  private destroy$ = new Subject<void>();
  
  constructor(private datasetService: DatasetService) {
    super();
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  protected initializeCell(): void {
    this.updateCheckboxConfiguration();
    this.initializeContext(); // Ensure context exists
    this.loadInitialValue();
  }
  
  private initializeContext(): void {
    // Initialize context for Checkbox cells that manage their own context
    if (this.dataSource === 'context' && this.contextObjectKey && this.viewManager) {
      // Ensure the context data object exists
      if (!this.viewManager.contextData) {
        this.viewManager.contextData = {};
      }
      
      // Initialize the context key if it doesn't exist
      if (!this.viewManager.contextData.hasOwnProperty(this.contextObjectKey)) {
        // Initialize with empty object that can hold field values
        this.viewManager.contextData[this.contextObjectKey] = {};
      }
    }
  }
  
  protected onCellOrDataChange(): void {
    this.updateCheckboxConfiguration();
    this.initializeContext(); // Ensure context exists
    this.loadInitialValue();
  }
  
  protected onCellUpdate(): void {
    this.updateCheckboxConfiguration();
    this.initializeContext(); // Ensure context exists
    this.loadInitialValue();
  }
  
  protected onDataSet(data: any): void {
    // Update value when external data changes
    this.loadInitialValue();
  }
  
  private updateCheckboxConfiguration(): void {
    if (!this.cell) return;
    
    // Get basic configuration
    this.labelText = this.getConfigValue('checkboxLabel', this.getConfigValue('label', 'Checkbox'));
    this.checkedValue = this.getConfigValue('checkedValue', true);
    this.uncheckedValue = this.getConfigValue('uncheckedValue', false);
    
    // Determine data source
    this.dataSource = this.getConfigValue('dataSource', 'none') as 'context' | 'dataset' | 'none';
    
    // Get data binding configuration
    this.contextObjectKey = this.getDataBindingValue('dataObjectKey') || '';
    this.fieldName = this.getDataBindingValue('fieldName') || '';
    this.datasetId = this.getDataBindingValue('datasetId') || null;
  }
  
  private loadInitialValue(): void {
    if (this.dataSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];
      if (dataObject) {
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          this.isChecked = this.valueToChecked(dataObject[0][this.fieldName]);
          // Normalize the stored value to the configured checked/unchecked value
          dataObject[0][this.fieldName] = this.checkedToValue(this.isChecked);
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          this.isChecked = this.valueToChecked(dataObject[this.fieldName]);
          // Normalize the stored value to the configured checked/unchecked value
          dataObject[this.fieldName] = this.checkedToValue(this.isChecked);
        }
      } else {
        this.isChecked = this.valueToChecked(this.uncheckedValue);
      }
    } else if (this.dataSource === 'dataset' && this.datasetId && this.fieldName) {
      // For dataset source, we might want to load data from the dataset
      // This would typically be handled by loading data from the service
      this.isChecked = this.valueToChecked(this.uncheckedValue);
    } else {
      // Independent field - value is managed locally
      const defaultValue = this.getConfigValue('defaultValue', this.uncheckedValue);
      this.isChecked = this.valueToChecked(defaultValue);
    }
  }
  
  private valueToChecked(value: any): boolean {
    // Convert stored value to checked state
    if (value === null || value === undefined) {
      return false;
    }

    // Check if the value matches the checked value (use loose comparison
    // so that "1" == 1, since context data from API may be stringified)
    // eslint-disable-next-line eqeqeq
    return value == this.checkedValue ||
           String(value) === String(this.checkedValue) ||
           (typeof value === 'boolean' && value) ||
           (typeof value === 'string' && value.toLowerCase() === 'true');
  }
  
  private checkedToValue(checked: boolean): any {
    return checked ? this.checkedValue : this.uncheckedValue;
  }
  
  onCheckboxChange(): void {
    const newValue = this.checkedToValue(this.isChecked);

    // Update the view manager context if configured
    if (this.dataSource === 'context' && this.contextObjectKey && this.fieldName && this.viewManager) {
      const dataObject = this.viewManager.contextData[this.contextObjectKey];

      if (dataObject) {
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          dataObject[0][this.fieldName] = newValue;
        } else if (typeof dataObject === 'object' && dataObject !== null) {
          dataObject[this.fieldName] = newValue;
        }
        // Note: We don't emit contextDataChange here because:
        // 1. The context object is already updated in-place
        // 2. All cells share the same viewManager reference
        // 3. Emitting would trigger unnecessary refreshes/API calls
        // The Action Button will read the updated context when saving
      }
    }
    // Note: For dataset and independent modes, we no longer emit changes
    // as this was triggering unnecessary refresh cycles
  }
}